/**
 * A multi-select of teams that APPLIES ON CLOSE (client, 21 Oct 2026: on the Adjustments tab
 * "when I select team, it will reload the page" — each tick refetched the grid, and the view's
 * loading state unmounted the open dropdown, so a second team could not be ticked).
 *
 * The ticks build a draft while the dropdown is open; `onChange` fires once, when it closes,
 * and only if the set changed. Removing a tag with its × or clearing everything happens with
 * the dropdown shut, so that applies at once — nobody is waiting to tick more. `[]` is every
 * team: the caller sends no filter, so a team created later is never left out.
 *
 * Options are `code · name` from W13's lookup (which excludes the `*` wildcard sentinel), the
 * label the job reports already print, so a search for `M1` finds M1.
 *
 * `nonTeamChoices` puts "Casual workers" and "No team" at the top (the payroll run's filters
 * only — client, 21 Oct 2026: casuals are on no team, "so how should we filter it"). They travel
 * inside the same list as `CASUAL` / `NO_TEAM`, and the API reads the set as a union.
 */
import { useEffect, useState } from 'react';
import { Select } from 'antd';
import { TEAM_FILTER_CASUAL, TEAM_FILTER_NO_TEAM, type TeamRefDto } from '@cropmate/shared';

export interface TeamMultiSelectProps {
  teams: readonly TeamRefDto[];
  value: readonly string[];
  onChange: (next: string[]) => void;
  loading?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
  /** Offer "Casual workers" and "No team" above the teams. Only where the API accepts them. */
  nonTeamChoices?: boolean;
}

const NON_TEAM_OPTIONS = [
  { value: TEAM_FILTER_CASUAL, label: 'Casual workers' },
  { value: TEAM_FILTER_NO_TEAM, label: 'No team' },
];

const sameSet = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((id) => b.includes(id));

export function TeamMultiSelect({
  teams,
  value,
  onChange,
  loading = false,
  size = 'small',
  style,
  nonTeamChoices = false,
}: TeamMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>([...value]);

  /* Follow the applied value while closed (a reset from outside, a URL change). */
  useEffect(() => {
    if (!open) setDraft([...value]);
  }, [value, open]);

  const commit = (next: readonly string[]) => {
    if (!sameSet(next, value)) onChange([...next]);
  };

  return (
    <Select
      mode="multiple"
      size={size}
      allowClear
      showSearch
      optionFilterProp="label"
      maxTagCount="responsive"
      style={{ minWidth: 220, maxWidth: 420, flex: '0 1 320px', ...style }}
      placeholder="All teams"
      loading={loading}
      open={open}
      value={draft}
      options={[
        ...(nonTeamChoices ? NON_TEAM_OPTIONS : []),
        ...teams.map((t) => ({ value: t.id, label: `${t.code} · ${t.name}` })),
      ]}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) commit(draft);
      }}
      onChange={(next: string[]) => {
        setDraft(next);
        if (!open) commit(next); // a tag's × or Clear, with the list shut
      }}
    />
  );
}
